/**
 * Berenberg brand style configuration — colors, typography, and prompt rules
 * extracted from the client's "AI slides vS.pptx" (slide master colorScheme
 * "Berenberg 2026" + fontScheme "Berenberg UK", layout placeholder geometry,
 * and real template charts, via the OOXML).
 *
 * Mirrors the shape of `cavendishStyleConfig.ts` / `evercoreStyleConfig.ts`.
 * When Berenberg style is active the prompt below is appended to the system
 * prompt and the color/theme constants are used as renderer defaults.
 *
 * Fidelity note: theme colors come from the master-linked colorScheme
 * "Berenberg 2026". The fontScheme names Arial, but slide usage is dominated
 * by "ES Peak" (bodyStyle latin on the master) with Arial as a secondary
 * face — THEME_COLORS.majorFont/minorFont therefore use ES Peak. Slide size
 * is A4-landscape (841.9×595.4 pt). Title/content geometry is from the
 * "Title and content" layout (the OOXML cSld name has a trailing space, but
 * catalog matching and AddSlide trim layout names, so the registry/prompt
 * must use the trimmed name). The wordmark is vector freeforms on slide
 * master 1, but that master's first layout is Cover, so content slides must
 * use contentLayoutName "Title and content" to inherit the branded body
 * layout rather than the cover. Template charts lead with orange
 * (theme dk2 / #FF5229).
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "Berenberg 2026" colorScheme
// ---------------------------------------------------------------------------

export const BERENBERG_COLORS = {
  orange: "FF5229", // dk2 — Berenberg signature orange (primary brand / chart lead)
  brown: "893B1F", // lt2
  slate: "576A85", // accent1 — slate blue-gray
  slateMid: "778AA7", // accent2
  slateLight: "A9B5C7", // accent3
  sage: "A6BDC2", // accent4
  taupe: "75726F", // accent5 / hlink
  taupeLight: "9D9A97", // accent6 / folHlink

  ink: "000000", // dk1 — titles & body text (black)
  white: "FFFFFF", // lt1

  ruleGray: "A6A6A6", // observed slide gray for rules / separators
  altRow: "F2F2F2", // light neutral for alternate-row table shading
} as const;

/**
 * Drop-in ThemeColors override when Berenberg style is active. Color slots
 * map 1:1 to the Berenberg 2026 colorScheme. Fonts follow observed slide
 * usage (ES Peak), not the generic Arial fontScheme.
 */
export const BERENBERG_THEME_COLORS: ThemeColors = {
  dark1: BERENBERG_COLORS.ink, // #000000 — Text1
  dark2: BERENBERG_COLORS.orange, // #FF5229 — Text2
  light1: BERENBERG_COLORS.white, // #FFFFFF — Background1
  light2: BERENBERG_COLORS.brown, // #893B1F — Background2
  accent1: BERENBERG_COLORS.slate, // #576A85
  accent2: BERENBERG_COLORS.slateMid, // #778AA7
  accent3: BERENBERG_COLORS.slateLight, // #A9B5C7
  accent4: BERENBERG_COLORS.sage, // #A6BDC2
  accent5: BERENBERG_COLORS.taupe, // #75726F
  accent6: BERENBERG_COLORS.taupeLight, // #9D9A97
  hyperlink: BERENBERG_COLORS.taupe,
  followedHyperlink: BERENBERG_COLORS.taupeLight,
  majorFont: "ES Peak",
  minorFont: "ES Peak",
};

/**
 * Ordered series palette. Template charts lead with orange (theme tx2 / dk2),
 * then slate accents for readable multi-series separation.
 */
export const BERENBERG_CHART_SERIES_PALETTE = [
  BERENBERG_COLORS.orange,
  BERENBERG_COLORS.slate,
  BERENBERG_COLORS.slateMid,
  BERENBERG_COLORS.sage,
  BERENBERG_COLORS.slateLight,
  BERENBERG_COLORS.taupe,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from master + "Title and content")
// ---------------------------------------------------------------------------

export const BERENBERG_SLIDE_DIMENSIONS = { width: 841.9, height: 595.4 } as const;

export const BERENBERG_POSITIONS = {
  title: { left: 24.0, top: 25.6, width: 793.8, height: 28.3 },
  contentStart: { left: 24.0, top: 90.8 },
  footnote: { left: 24.0, top: 554.3, width: 635.0, height: 21.2 },
} as const;

// Authoritative layout names from the Berenberg master. Catalog matching and
// AddSlide trim, so names here match the trimmed catalog rather than the raw
// OOXML cSld/@name (a few layouts store a trailing space).
export const BERENBERG_LAYOUT_NAMES = [
  "Cover",
  "Title and content (blue)",
  "Two column (blue)",
  "Two column 2 (blue)",
  "One column two box (blue)",
  "Two row (blue)",
  "One row two box (blue)",
  "Four box (blue)",
  "Title and content (red)",
  "Two column (red)",
  "Two column 2 (red)",
  "One column two box (red)",
  "Two row (red)",
  "One row two box (red)",
  "Four box (red)",
  "Title and content",
  "Two column",
  "Two row",
  "Title only",
  "Custom Layout",
  "Back cover",
  "2_Title only",
  "1_Title only",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when Berenberg style is active
// ---------------------------------------------------------------------------

export const BERENBERG_STYLE_PROMPT = `

## BERENBERG BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Berenberg.pptx" contains the current Berenberg visual standard. Match the closest relevant precedent for covers, one-/two-column content, charts, and notes/sources footers. Prefer its title/subtitle hierarchy, bottom wordmark, orange-led charts, and slate tables over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Berenberg uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts, notes: \`"ES Peak"\`
NEVER omit fontFamily when Berenberg style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the Berenberg master colorScheme "Berenberg 2026")
Use ONLY these Berenberg brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${BERENBERG_COLORS.ink}
- Text2 / dark2: Orange #${BERENBERG_COLORS.orange}
- Background1 / light1: White #${BERENBERG_COLORS.white}
- Background2 / light2: Brown #${BERENBERG_COLORS.brown}
- Accent1: Slate #${BERENBERG_COLORS.slate}
- Accent2: Mid slate #${BERENBERG_COLORS.slateMid}
- Accent3: Light slate #${BERENBERG_COLORS.slateLight}
- Accent4: Sage #${BERENBERG_COLORS.sage}
- Accent5: Taupe #${BERENBERG_COLORS.taupe}
- Accent6: Light taupe #${BERENBERG_COLORS.taupeLight}

**Usage:**
- **Primary brand / emphasis**: Orange #${BERENBERG_COLORS.orange}
- **Secondary emphasis**: Slate #${BERENBERG_COLORS.slate}
- **Text / ink**: Black #${BERENBERG_COLORS.ink}
- **Lines & borders**: Gray #${BERENBERG_COLORS.ruleGray}
- **Background / shading**: Light gray #${BERENBERG_COLORS.altRow}
- **Chart series palette** (in order): ${BERENBERG_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with orange (#${BERENBERG_COLORS.orange}) as the first/primary series color.

### Typography (authoritative — from the Berenberg master / "Title and content" layout)
Slide dimensions: 841.9 × 595.4 pt (A4 landscape).

**Placeholder positions:**
- **Title**: left=24.0, top=25.6, width=793.8, height=28.3 — "ES Peak" ~18-20pt, black (#${BERENBERG_COLORS.ink}), left-aligned
- **Subtitle**: left=24.0, top=56.9, width=793.8, height=17.0 — "ES Peak" 16pt
- **Content area**: starts at left=24.0, top=90.8, width=793.7, height=456.4
- **Notes / sources**: left=24.0, top=554.3, width=635.0, height=21.2 — "ES Peak" 7pt
- **Page number**: bottom band around top=554 — "ES Peak" 10pt bold

**Content text hierarchy ("ES Peak", black #${BERENBERG_COLORS.ink}, ~10pt body):**
- Keep body text ~10-12pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use bold or the orange/slate accents; bullets otherwise inherit black text.

### Charts (Berenberg style)
- Font: "ES Peak", minimum 8pt
- **Primary data color**: orange (#${BERENBERG_COLORS.orange}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Berenberg style)
- Header row: slate (#${BERENBERG_COLORS.slate}) fill with white text, bold
- Body: white fill, black "ES Peak" text (10pt)
- Alternate-row shading: light gray (#${BERENBERG_COLORS.altRow})
- Borders: thin gray (#${BERENBERG_COLORS.ruleGray}); align numeric columns consistently

### Shapes & layout (Berenberg style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin gray (#${BERENBERG_COLORS.ruleGray}) rule lines for separation when needed
- Connectors: solid gray (#${BERENBERG_COLORS.ruleGray}) lines
- Page numbers / notes: 7-10pt, "ES Peak"
- Prefer layout "Title and content" for body slides. Do not default to "Cover".

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
